/**
 * AssignMemberPicker — Quick picker to assign an existing user to report to someone.
 *
 * Shows a searchable list of org members. Click to assign them.
 * Prioritizes unassigned users at the top, then sorted alphabetically.
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import Users from 'lucide-react/dist/esm/icons/users';
import GitBranch from 'lucide-react/dist/esm/icons/git-branch';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import Check from 'lucide-react/dist/esm/icons/check';
import type { OrgUser } from '../../../services/api/org';
import { Button, Modal, SearchInput, Spinner } from '../../../components/ui';

interface AssignMemberPickerProps {
  isOpen: boolean;
  onClose: () => void;
  /** The person we're assigning members TO (the manager) */
  targetUser: { id: string; display_name: string | null; email: string } | null;
  /** All org users */
  users: OrgUser[];
  /** Called when a user is picked — assigns them to report to targetUser */
  onAssign: (userId: string, managerId: string) => Promise<void>;
}

const AssignMemberPicker: React.FC<AssignMemberPickerProps> = ({
  isOpen,
  onClose,
  targetUser,
  users,
  onAssign,
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [assigning, setAssigning] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setAssigning(null);
      setAssigned(new Set());
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Available users: active, not the target, not already reporting to target
  const available = useMemo(() => {
    if (!targetUser) return [];
    return users.filter(
      (u) =>
        u.id !== targetUser.id &&
        u.status !== 'deactivated' &&
        u.reports_to !== targetUser.id
    );
  }, [users, targetUser]);

  // Filter by search
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return available;
    return available.filter(
      (u) =>
        (u.display_name || '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.title || '').toLowerCase().includes(q)
    );
  }, [available, search]);

  // Sort: unassigned first, then alphabetical
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aUnassigned = !a.reports_to ? 0 : 1;
      const bUnassigned = !b.reports_to ? 0 : 1;
      if (aUnassigned !== bUnassigned) return aUnassigned - bUnassigned;
      return (a.display_name || a.email).localeCompare(b.display_name || b.email);
    });
  }, [filtered]);

  const handleAssign = async (userId: string) => {
    if (!targetUser || assigning) return;
    setAssigning(userId);
    try {
      await onAssign(userId, targetUser.id);
      setAssigned((prev) => new Set(prev).add(userId));
    } finally {
      setAssigning(null);
    }
  };

  if (!targetUser) return null;

  const targetName = targetUser.display_name || targetUser.email.split('@')[0];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-content flex items-center gap-2">
            <GitBranch size={16} className="text-primary" />
            Assign Team Member
          </h2>
          <p className="text-xs text-content-muted mt-1">
            Select someone to report to <span className="text-content-secondary font-medium">{targetName}</span>
          </p>
        </div>
      </Modal.Header>

      {/* Search */}
      <div className="px-6 py-3">
        <SearchInput
          ref={searchRef}
          value={search}
          onChange={setSearch}
          placeholder="Search by name, email, or title..."
          onClear={() => setSearch('')}
        />
      </div>

      {/* User List */}
      <div className="flex-1 overflow-y-auto px-6 pb-4 min-h-0 max-h-[50vh]">
        <div className="space-y-1">
          {sorted.map((user) => {
            const isAssigning = assigning === user.id;
            const justAssigned = assigned.has(user.id);
            const isUnassigned = !user.reports_to;
            const currentManager = user.reports_to
              ? users.find((u) => u.id === user.reports_to)
              : null;

            return (
              <button
                key={user.id}
                type="button"
                onClick={() => !justAssigned && handleAssign(user.id)}
                disabled={isAssigning || justAssigned}
                className={`
                  w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all border outline-none
                  ${justAssigned
                    ? 'bg-status-success/10 border-status-success/20 cursor-default'
                    : isUnassigned
                      ? 'bg-amber-400/[0.03] border-amber-400/10 hover:border-amber-400/20 cursor-pointer'
                      : 'bg-transparent border-subtle hover:bg-surface-primary hover:border-subtle cursor-pointer'
                  }
                `}
              >
                {/* Avatar */}
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0
                  ${justAssigned
                    ? 'bg-status-success/20 text-status-success'
                    : isUnassigned
                      ? 'bg-amber-400/10 text-amber-400'
                      : 'bg-surface-secondary text-content-tertiary'
                  }
                `}>
                  {justAssigned
                    ? <Check size={14} />
                    : (user.display_name || user.email)[0]?.toUpperCase()
                  }
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-content truncate">
                      {user.display_name || user.email.split('@')[0]}
                    </span>
                    {isUnassigned && !justAssigned && (
                      <AlertTriangle size={10} className="text-amber-400/50 shrink-0" />
                    )}
                  </div>
                  <div className="text-[10px] text-content-muted truncate">
                    {user.title || user.email}
                    {currentManager && !justAssigned && (
                      <span className="text-content-faint">
                        {' · reports to '}
                        {currentManager.display_name || currentManager.email.split('@')[0]}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status */}
                {isAssigning && (
                  <Spinner size="sm" className="shrink-0" />
                )}
                {justAssigned && (
                  <span className="text-[10px] text-status-success font-medium shrink-0">Assigned</span>
                )}
              </button>
            );
          })}

          {sorted.length === 0 && (
            <div className="text-center py-10">
              <Users size={24} className="text-content-faint mx-auto mb-2" />
              <p className="text-xs text-content-muted">
                {search ? 'No matching members' : 'Everyone is already assigned'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Modal.Footer>
        <Button variant="ghost" size="lg" onClick={onClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(AssignMemberPicker);
